import { Entity } from '@backstage/catalog-model';
import { featureFlagsApiRef, useApi } from '@backstage/core-plugin-api';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import {
  AIProvisioningServiceApiRef,
  AzureManagementServiceApiRef,
  BucketId,
  ClientId,
  aaPingSSOAuthApiRef,
  useTrackedRequest,
} from '@runway/devkit';
import { PluginId } from '@runway/devkit-common';
import axios from 'axios';
import { useState } from 'react';
import { useAsync, useAsyncFn } from 'react-use';
import { ArgoCDServiceApiRef } from '../../services/argocd.catalog.service';
import { catalogProjectsServiceApiRef } from '../../services/catalogProjects.service';
import { RegisterAppForm, RegisterAppFormValues } from './RegisterAppForm';

interface RegisterAppDialogProps {
  isOpen: boolean;
  setRegisterAppComponentOpen: any;
  setSnackbarOpen: any;
  setSnackbarMessage: (message: string | undefined) => void;
  onDeploymentAdded: () => void;
  close(): void;
  entity: Entity;
}

const getEntityOrg = (entity: Entity): string =>
  entity.metadata.annotations?.['github.com/project-slug']?.split('/')[0] ?? 'AAInternal';

export const RegisterAppDialog = ({
  isOpen,
  setRegisterAppComponentOpen,
  setSnackbarOpen,
  setSnackbarMessage,
  onDeploymentAdded,
  close,
  entity,
}: RegisterAppDialogProps) => {
  const argoCatalog = useApi(ArgoCDServiceApiRef);
  const amsApi = useApi(AzureManagementServiceApiRef);
  const identity = useApi(aaPingSSOAuthApiRef);
  const provisioningService = useApi(AIProvisioningServiceApiRef);
  const catalogProjectsService = useApi(catalogProjectsServiceApiRef);
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const { tracker } = useTrackedRequest({
    bucket_id: BucketId.USE_METRIC,
    client_id: ClientId.RUNWAY_FRONTEND,
    pluginId: PluginId.CATALOG_DEPLOY_ARGO_APP.id,
  });

  const isEmuEnabled = featureFlagsApi.isActive('github-emu-auth');
  const [selectedOrg, setSelectedOrg] = useState(isEmuEnabled ? getEntityOrg(entity) : 'AAInternal');
  const [hideResult, setHideResult] = useState(true);
  const [pendingSuccessMessage, setPendingSuccessMessage] = useState<string>();

  const appName = entity.metadata.name;
  const isAIAgentService = entity.kind?.toLowerCase() === 'aiagent';
  const aiCategory = (entity as { spec?: { aiInfo?: { aiCategory?: string } } }).spec?.aiInfo?.aiCategory;

  const aiConfigState = useAsync(async () => {
    if (!isOpen || !isAIAgentService || !aiCategory) return null;
    const appShortname = await provisioningService.resolveAppShortname(catalogProjectsService, entity, appName);
    const config = await provisioningService.fetchAIConfig(appShortname, aiCategory);
    return { config, appShortname };
  }, [isOpen, isAIAgentService, aiCategory, provisioningService, catalogProjectsService, entity, appName]);

  const handleClose = () => {
    setHideResult(true);
    setPendingSuccessMessage(undefined);
    close();
  };

  const handleDialogExited = () => {
    if (!pendingSuccessMessage) return;

    setSnackbarMessage(pendingSuccessMessage);
    setSnackbarOpen(true);
    setPendingSuccessMessage(undefined);
    onDeploymentAdded();
  };

  const buildArgoErrorString = (
    detail: undefined | { message: string; group_names: string[]; group_ids: string[] },
  ) => {
    const defaultMessage = 'You need proper AAD Group access to proceed.';
    if (!detail) return defaultMessage;
    if (detail.group_names?.length)
      return `You need membership in one of the following AAD Groups: ${detail.group_names.join(', ')}`;
    if (detail.group_ids?.length)
      return `You need membership in one of the following AAD Groups: ${detail.group_ids.join(', ')}`;
    return defaultMessage;
  };

  const [submitState, doSubmit] = useAsyncFn(
    async (values: RegisterAppFormValues) => {
      try {
        setHideResult(false);

        if (!entity?.metadata?.annotations?.['argocd/app-selector']) {
          throw new Error(
            "Missing 'metadata.annotations.argocd/app-selector' in 'catalog-info.yaml'. Please add this field and use the format 'backstage-name=<app-name>' for the value.",
          );
        }

        const req = {
          clusterName: values.hostCluster,
          namespace: values.namespace,
          projectName: values.appName,
          appName: values.appName,
          sourceRepo: `https://github.com/${selectedOrg}/${values.sourceRepo}.git`,
          sourcePath: values.sourcePath,
          labelValue: entity?.metadata?.annotations?.['argocd/app-selector'].replace('backstage-name=', ''),
        };

        if (isAIAgentService && values.createAIInfrastructure && values.aiFoundrySelection?.foundry) {
          const environment = values.hostCluster.includes('-p-') ? 'prod' : 'nonprod';
          const resolvedAppShortname =
            aiConfigState.value?.appShortname ??
            (await provisioningService.resolveAppShortname(catalogProjectsService, entity, appName));
          await provisioningService.provisionResources(
            {
              entityName: entity.metadata.name,
              appShortname: resolvedAppShortname,
              aiCategory: aiCategory ?? '',
              foundry: values.aiFoundrySelection.foundry,
              aadGroup: values.aiFoundrySelection.aadGroup,
              environment,
            },
            amsApi,
          );
        }

        await tracker(
          async () => {
            await argoCatalog.createArgoApp(req);
          },
          {
            userEmployeeId: (await identity.getAAProfile())?.aaId ?? '-1',
            clusterName: values.hostCluster,
            namespace: values.namespace,
            appName: values.appName,
            sourceRepo: values.sourceRepo,
            sourcePath: values.sourcePath,
          },
        );

        const successMessage =
          isAIAgentService && values.createAIInfrastructure
            ? 'AI infrastructure request submitted. Deployment may still be pending.'
            : 'Application created successfully. Deployment may still be pending.';
        setPendingSuccessMessage(successMessage);
        setRegisterAppComponentOpen(false);
        return successMessage;
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          if (e.response?.status === 403) {
            throw new Error(
              `Error Creating ArgoCD Application - Insufficient Permissions. ${buildArgoErrorString(
                e.response.data.detail,
              )}`,
            );
          }
          if (e.response?.data?.message) {
            throw new Error(e.response?.data?.message);
          }
        }
        throw new Error(e.message);
      }
    },
    [selectedOrg],
  );

  return (
    <Dialog
      open={isOpen}
      fullWidth
      keepMounted
      onClose={handleClose}
      TransitionProps={{ onExited: handleDialogExited }}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>Deploy ArgoCD App</DialogTitle>

      <DialogContent>
        <RegisterAppForm
          appName={appName}
          hideResult={hideResult}
          handleClose={handleClose}
          submitState={submitState}
          doSubmit={doSubmit}
          entityOrg={selectedOrg}
          isAIAgentService={isAIAgentService}
          aiConfigData={aiConfigState.value?.config ?? null}
          aiConfigLoading={aiConfigState.loading}
          aiConfigError={aiConfigState.error?.message}
          isEmuEnabled={isEmuEnabled}
          onOrganizationChange={organization => setSelectedOrg(organization)}
        />
      </DialogContent>
    </Dialog>
  );
};
